import React, { useEffect, useState } from "react";
import { supabase } from "@/lib/supabaseClient";
import { useAuth } from "@/lib/AuthContext";
import { ModalShell, Field, TextInput, SelectInput, ModalFooter, ErrorBanner } from "../shared/FormElements";

const UNIT_OPTIONS = ["currency", "count", "percentage", "ratio"];

export default function ObjectiveTemplates() {
  const { profile } = useAuth();

  const [metrics, setMetrics] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showMetricForm, setShowMetricForm] = useState(false);
  const [showTemplateForm, setShowTemplateForm] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    setLoading(true);
    setError(null);
    const [metricsRes, templatesRes] = await Promise.all([
      supabase.from("master_metrics").select("*").order("display_name"),
      supabase.from("project_types").select("*").order("created_at"),
    ]);
    if (metricsRes.error) setError(metricsRes.error.message);
    if (templatesRes.error) setError(templatesRes.error.message);
    setMetrics(metricsRes.data || []);
    setTemplates(templatesRes.data || []);
    setLoading(false);
  }

  function metricLabel(key) {
    const found = metrics.find((m) => m.key === key);
    return found ? found.display_name : key;
  }

  if (loading) {
    return <p className="text-sm text-muted-foreground">Loading objective templates...</p>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <h1 className="text-xl font-semibold text-foreground">Objective templates</h1>
      </div>
      <p className="text-sm text-muted-foreground mb-6">
        Both admin and staff can create new templates
      </p>

      <ErrorBanner error={error} />

      <div className="grid grid-cols-[1.4fr_1fr] gap-5 items-start">
        <div>
          <button
            onClick={() => setShowTemplateForm(true)}
            className="bg-primary text-primary-foreground rounded-lg px-4 py-2 text-sm font-semibold mb-4"
          >
            + New objective template
          </button>

          <div className="flex flex-col gap-3">
            {templates.map((t) => (
              <div key={t.id} className="border border-border rounded-2xl p-4 bg-card">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold text-sm text-foreground">{t.name}</span>
                  <span className="font-mono text-xs text-muted-foreground">
                    {t.default_metric_keys.length} metric{t.default_metric_keys.length !== 1 ? "s" : ""}
                  </span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {t.default_metric_keys.length === 0 ? (
                    <span className="text-xs text-muted-foreground">Pick per project</span>
                  ) : (
                    t.default_metric_keys.map((key) => (
                      <span
                        key={key}
                        className="text-xs bg-accent text-foreground rounded-full px-2.5 py-1"
                      >
                        {metricLabel(key)}
                      </span>
                    ))
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="border border-border rounded-2xl bg-card overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-border">
            <span className="font-semibold text-sm text-foreground">Master metric list</span>
            <button
              onClick={() => setShowMetricForm(true)}
              className="text-xs border border-input rounded-lg px-3 py-1.5"
            >
              + Add metric
            </button>
          </div>
          <table className="w-full text-sm">
            <tbody>
              {metrics.map((m) => (
                <tr key={m.id} className="border-b border-border last:border-0">
                  <td className="px-5 py-3 text-foreground">{m.display_name}</td>
                  <td className="px-5 py-3 text-right">
                    <span className="font-mono text-xs text-muted-foreground">{m.unit}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showMetricForm && (
        <AddMetricModal
          onClose={() => setShowMetricForm(false)}
          onSaved={() => {
            setShowMetricForm(false);
            loadData();
          }}
        />
      )}

      {showTemplateForm && (
        <AddTemplateModal
          metrics={metrics}
          adminUserId={profile?.id}
          onClose={() => setShowTemplateForm(false)}
          onSaved={() => {
            setShowTemplateForm(false);
            loadData();
          }}
        />
      )}
    </div>
  );
}

function AddMetricModal({ onClose, onSaved }) {
  const [key, setKey] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [unit, setUnit] = useState(UNIT_OPTIONS[0]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  async function handleSave() {
    if (!key || !displayName) {
      setError("Key and display name are both required.");
      return;
    }
    setSaving(true);
    const { error: insertError } = await supabase
      .from("master_metrics")
      .insert({ key: key.trim().toLowerCase().replace(/\s+/g, "_"), display_name: displayName.trim(), unit });
    setSaving(false);
    if (insertError) {
      setError(insertError.message);
      return;
    }
    onSaved();
  }

  return (
    <ModalShell title="Add metric" onClose={onClose}>
      <ErrorBanner error={error} />
      <Field label="Display name">
        <TextInput value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="e.g. Click-through rate" />
      </Field>
      <Field label="Key (used internally, auto-formatted)">
        <TextInput value={key} onChange={(e) => setKey(e.target.value)} placeholder="e.g. ctr" />
      </Field>
      <Field label="Unit">
        <SelectInput value={unit} onChange={(e) => setUnit(e.target.value)}>
          {UNIT_OPTIONS.map((u) => (
            <option key={u} value={u}>
              {u}
            </option>
          ))}
        </SelectInput>
      </Field>
      <ModalFooter onClose={onClose} onSave={handleSave} saving={saving} />
    </ModalShell>
  );
}

function AddTemplateModal({ metrics, adminUserId, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  function toggle(key) {
    setSelected((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  }

  async function handleSave() {
    if (!name) {
      setError("Name is required.");
      return;
    }
    setSaving(true);
    const { error: insertError } = await supabase.from("project_types").insert({
      name: name.trim(),
      default_metric_keys: selected,
      applicable_tabs: ["Overview", "Reports", "Invoices", "Account"],
      created_by: adminUserId,
    });
    setSaving(false);
    if (insertError) {
      setError(insertError.message);
      return;
    }
    onSaved();
  }

  return (
    <ModalShell title="New objective template" onClose={onClose}>
      <ErrorBanner error={error} />
      <Field label="Objective name">
        <TextInput value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Content Marketing" />
      </Field>
      <Field label="Default metrics">
        <div className="flex flex-col gap-1.5 max-h-48 overflow-y-auto border border-border rounded-lg p-2">
          {metrics.map((m) => (
            <label key={m.key} className="flex items-center gap-2 text-sm px-2 py-1">
              <input type="checkbox" checked={selected.includes(m.key)} onChange={() => toggle(m.key)} />
              {m.display_name}
            </label>
          ))}
        </div>
      </Field>
      <ModalFooter onClose={onClose} onSave={handleSave} saving={saving} />
    </ModalShell>
  );
}
